/**
 * Whether an item has somewhere to go — the one rule behind every link to a publication.
 *
 * **Its own module, and not inside `ItemLink.tsx`, for two reasons.** The rule is not JSX, and
 * `make web-check` runs on plain `node`, which strips types from `.ts` but cannot parse `.tsx`.
 * The same split `nextstep.ts` has, for the same reason: every wrong answer this can give
 * still renders perfectly, so `tsc` will never catch one and a check has to.
 *
 * It exists because the guard was written three times and missed the fourth. The Log's Title
 * cell, the Log's URL cell and the Review queue each tested `item.url` before rendering an
 * anchor; the Actions screen did not, and rendered `<a href="">` for any item without one.
 * An empty `href` is not an inert link — it points at the current page. So that link copied as
 * `https://horizon.norwoodcompliance.co.uk/`, and clicking it *reloaded the app*, which resets
 * `tab` to its default and lands on the Review queue. Two reported symptoms, one missing falsy
 * check (Joanne and Craig, 31 August 2026).
 *
 * 96 of her tracked items have no URL, every one a workbook import whose spreadsheet cell
 * carried no hyperlink — so on the Actions screen this is the ordinary case, not an edge one.
 */

/**
 * Stricter than truthiness, and both extra conditions are real rather than tidy:
 *
 * - **Whitespace is not an address.** `"  "` is truthy, and `href=" "` resolves to the current
 *   page exactly as `href=""` does — the same bug wearing a value that passes a truthy test.
 * - **Only http(s).** These come from an imported spreadsheet and from fetched pages, which
 *   `CLAUDE.md` classes as untrusted input. A `javascript:` or `data:` value in one of her
 *   cells would otherwise become a click target in her browser.
 */
export function followable(url?: string | null): boolean {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return false;
  return /^https?:\/\//i.test(trimmed);
}
